"use client";

import { useSearchParams } from "next/navigation";
import { useState } from "react";
import type { ReportCategory } from "@/lib/api-schemas";

const CATEGORIES: readonly { id: ReportCategory; label: string }[] = [
  { id: "harassment", label: "Harassment or abuse" },
  { id: "copyright", label: "Copyright or ownership" },
  { id: "minors_safety", label: "Minors or safety concern" },
  { id: "marine_harm", label: "Harmful wildlife / habitat content" },
  { id: "spam", label: "Spam or scam" },
  { id: "other", label: "Other" },
];

export function ReportForm() {
  const searchParams = useSearchParams();
  const initialUrl = searchParams.get("url")?.trim() ?? "";

  const [category, setCategory] = useState<ReportCategory>("other");
  const [pageUrl, setPageUrl] = useState(initialUrl);
  const [description, setDescription] = useState("");
  const [reporterEmail, setReporterEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "done" | "error">(
    "idle",
  );
  const [message, setMessage] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStatus("sending");
    setMessage(null);
    try {
      const res = await fetch("/api/reports", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          category,
          description,
          pageUrl: pageUrl || "/",
          reporterEmail: reporterEmail.trim() || undefined,
        }),
      });
      const j = (await res.json().catch(() => ({}))) as {
        error?: string;
        details?: unknown;
      };
      if (!res.ok) {
        setStatus("error");
        setMessage(
          typeof j.error === "string"
            ? j.error
            : "Could not submit report. Check fields.",
        );
        return;
      }
      setStatus("done");
      setMessage("Thanks — your report was recorded.");
      setDescription("");
    } catch {
      setStatus("error");
      setMessage("Network error. Try again.");
    }
  }

  if (status === "done") {
    return (
      <div className="rounded-2xl border border-teal-400/30 bg-teal-950/20 px-6 py-8 text-teal-100">
        <p className="font-medium text-white">{message}</p>
        <p className="mt-2 text-sm text-teal-200/80">
          Demo queue: operators review in the dashboard moderation view (no auth).
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={(ev) => void onSubmit(ev)}
      className="mx-auto max-w-lg space-y-6 rounded-2xl border border-white/10 bg-black/20 p-6 sm:p-8"
    >
      <div>
        <label className="block text-xs font-medium uppercase tracking-wide text-teal-200/80">
          Category
        </label>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value as ReportCategory)}
          className="mt-2 w-full rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-white focus:border-teal-400/50 focus:outline-none"
        >
          {CATEGORIES.map((c) => (
            <option key={c.id} value={c.id}>
              {c.label}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-xs font-medium uppercase tracking-wide text-teal-200/80">
          Page URL or path
        </label>
        <input
          type="text"
          value={pageUrl}
          onChange={(e) => setPageUrl(e.target.value)}
          required
          placeholder="https://… or /reef"
          className="mt-2 w-full rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-white placeholder:text-teal-200/40 focus:border-teal-400/50 focus:outline-none"
        />
      </div>
      <div>
        <label className="block text-xs font-medium uppercase tracking-wide text-teal-200/80">
          What happened? (min. 10 characters)
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          required
          minLength={10}
          rows={5}
          className="mt-2 w-full rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-white placeholder:text-teal-200/40 focus:border-teal-400/50 focus:outline-none"
          placeholder="Include links, timestamps, or usernames if relevant."
        />
      </div>
      <div>
        <label className="block text-xs font-medium uppercase tracking-wide text-teal-200/80">
          Your email (optional)
        </label>
        <input
          type="email"
          value={reporterEmail}
          onChange={(e) => setReporterEmail(e.target.value)}
          className="mt-2 w-full rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-white placeholder:text-teal-200/40 focus:border-teal-400/50 focus:outline-none"
          placeholder="So we can follow up"
        />
      </div>
      {message && status === "error" && (
        <p className="text-sm text-amber-200/90" role="alert">
          {message}
        </p>
      )}
      <button
        type="submit"
        disabled={status === "sending"}
        className="w-full rounded-xl bg-teal-400 px-4 py-3 font-semibold text-teal-950 shadow-lg transition hover:bg-teal-300 disabled:opacity-60"
      >
        {status === "sending" ? "Submitting…" : "Submit report"}
      </button>
    </form>
  );
}
